import { useState, useEffect } from 'react';
import { AlertOctagon, MessageSquare, CheckCircle, Clock, AlertTriangle, Search, Filter } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface Ticket {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high';
  category: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  created_at: string;
  updated_at: string;
  admin_response?: string;
  admin_notes?: string;
}

export function SupportTicketsView() {
  const { profile } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [response, setResponse] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (profile?.role === 'Superadmin') {
      fetchTickets();
    }
  }, [profile]);

  const fetchTickets = async () => {
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tickets:', error);
      return;
    }

    setTickets(data || []);
  };

  const updateTicketStatus = async (ticketId: string, status: string) => {
    const { error } = await supabase
      .from('support_tickets')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', ticketId);

    if (error) {
      console.error('Error updating ticket:', error);
      alert('Failed to update ticket status');
      return;
    }

    fetchTickets();
    if (selectedTicket?.id === ticketId) {
      setSelectedTicket({ ...selectedTicket, status: status as any });
    }
  };

  const submitResponse = async () => {
    if (!selectedTicket || !response.trim()) return;

    setIsSubmitting(true);
    try {
      // Update ticket with response
      const { error: updateError } = await supabase
        .from('support_tickets')
        .update({
          admin_response: response,
          admin_notes: adminNotes,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedTicket.id);

      if (updateError) throw updateError;

      // Send email response via GoHighLevel
      const { data: ghlSettings } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_key', 'gohighlevel_api_key')
        .single();

      const ghlApiKey = ghlSettings?.setting_value;
      if (ghlApiKey) {
        await fetch('https://rest.gohighlevel.com/v1/conversations/messages', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${ghlApiKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            type: 'Email',
            contactId: selectedTicket.user_email,
            message: `
              <h2>Response to Your Support Ticket #${selectedTicket.id}</h2>
              <p>Hello ${selectedTicket.user_name},</p>
              <p><strong>Original Issue:</strong> ${selectedTicket.title}</p>
              <hr>
              <p><strong>Our Response:</strong></p>
              <p>${response}</p>
              <hr>
              <p>If you have any further questions, please reply to this email or create a new ticket.</p>
              <p>Best regards,<br>TMS Support Team</p>
            `,
            subject: `Re: [TMS Support] Ticket #${selectedTicket.id} - ${selectedTicket.title}`
          })
        });
      }

      alert('Response sent successfully!');
      setResponse('');
      setAdminNotes('');
      fetchTickets();
    } catch (error) {
      console.error('Error submitting response:', error);
      alert('Failed to send response');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesSearch = ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ticket.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ticket.user_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-blue-600 bg-blue-100';
      case 'in_progress': return 'text-yellow-600 bg-yellow-100';
      case 'resolved': return 'text-green-600 bg-green-100';
      case 'closed': return 'text-slate-600 bg-slate-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  if (profile?.role !== 'Superadmin') {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
        <p className="text-slate-600">Only Superadmin can access support tickets.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-red-100 p-3 rounded-lg">
            <AlertOctagon className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Support Tickets</h2>
            <p className="text-slate-600">Manage and respond to user problem reports</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        {/* Tickets List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tickets List Column */}
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedTicket?.id === ticket.id
                    ? 'border-red-600 bg-red-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">#{ticket.id.slice(0, 8)} - {ticket.title}</h3>
                    <p className="text-sm text-slate-600">{ticket.user_name} ({ticket.user_email})</p>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority.toUpperCase()}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getStatusColor(ticket.status)}`}>
                      {ticket.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-slate-700 line-clamp-2 mb-2">{ticket.description}</p>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>{ticket.category}</span>
                  <span>•</span>
                  <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
            {filteredTickets.length === 0 && (
              <div className="text-center py-12">
                <AlertOctagon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No tickets found</p>
              </div>
            )}
          </div>

          {/* Ticket Detail Column */}
          <div className="border-2 border-slate-200 rounded-lg p-6">
            {selectedTicket ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    Ticket #{selectedTicket.id.slice(0, 8)}
                  </h3>
                  <div className="flex gap-2 mb-4">
                    <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getPriorityColor(selectedTicket.priority)}`}>
                      {selectedTicket.priority.toUpperCase()}
                    </span>
                    <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getStatusColor(selectedTicket.status)}`}>
                      {selectedTicket.status.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className="text-xs px-3 py-1 rounded-full font-semibold bg-slate-100 text-slate-700">
                      {selectedTicket.category}
                    </span>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">User Information</h4>
                  <p className="text-sm text-slate-700">Name: {selectedTicket.user_name}</p>
                  <p className="text-sm text-slate-700">Email: {selectedTicket.user_email}</p>
                  <p className="text-sm text-slate-700">
                    Submitted: {new Date(selectedTicket.created_at).toLocaleString()}
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Problem Description</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                {selectedTicket.admin_response && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">Previous Response</h4>
                    <p className="text-sm text-green-800 whitespace-pre-wrap">{selectedTicket.admin_response}</p>
                  </div>
                )}

                {selectedTicket.admin_notes && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-2">Admin Notes</h4>
                    <p className="text-sm text-yellow-800 whitespace-pre-wrap">{selectedTicket.admin_notes}</p>
                  </div>
                )}

                {/* Status Update Buttons */}
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Update Status</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => updateTicketStatus(selectedTicket.id, 'open')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
                    >
                      <Clock className="w-4 h-4 inline mr-1" />
                      Open
                    </button>
                    <button
                      onClick={() => updateTicketStatus(selectedTicket.id, 'in_progress')}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-semibold"
                    >
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      In Progress
                    </button>
                    <button
                      onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold"
                    >
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Resolved
                    </button>
                    <button
                      onClick={() => updateTicketStatus(selectedTicket.id, 'closed')}
                      className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-semibold"
                    >
                      Closed
                    </button>
                  </div>
                </div>

                {/* Response Form */}
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Send Response</h4>
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[100px] mb-2"
                    placeholder="Type your response to the user..."
                  />
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[60px] mb-2"
                    placeholder="Internal admin notes (not sent to user)..."
                  />
                  <button
                    onClick={submitResponse}
                    disabled={isSubmitting || !response.trim()}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="w-5 h-5" />
                        Send Response
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Select a ticket to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
